'use client'

import { Skeleton } from '@/components/ui/skeleton'
import {
  useDeleteScheduledTaskMutation,
  useGetScheduledTasksQuery,
  useSkipNextScheduledTaskMutation,
  useUpdateScheduledTaskMutation,
  type UpdateScheduledTaskInput,
} from '@/lib/features/scheduled-tasks/scheduledTasksApi'
import Link from 'next/link'
import { useCallback, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { ScheduledTaskCard } from '../../app/scheduled-tasks/ScheduledTaskCard'
import { ScheduledTaskModal } from '../../app/scheduled-tasks/ScheduledTaskModal'

const MAX_CARDS = 6

const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' })

function formatRelativeTime(date: Date | string | null | undefined): string {
  if (!date) return 'Never'
  const diffSecs = (new Date(date).getTime() - Date.now()) / 1000
  if (Math.abs(diffSecs) < 60) return rtf.format(Math.round(diffSecs), 'seconds')
  if (Math.abs(diffSecs) < 3600) return rtf.format(Math.round(diffSecs / 60), 'minutes')
  if (Math.abs(diffSecs) < 86400) return rtf.format(Math.round(diffSecs / 3600), 'hours')
  return rtf.format(Math.round(diffSecs / 86400), 'days')
}

export default function DashboardScheduledTasks() {
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null)

  const { data: tasksData, isLoading } = useGetScheduledTasksQuery(
    { limit: 50 },
    { refetchOnMountOrArgChange: true }
  )

  const [deleteTask] = useDeleteScheduledTaskMutation()
  const [updateTask] = useUpdateScheduledTaskMutation()
  const [skipNextTask] = useSkipNextScheduledTaskMutation()

  const handleDeleteTask = useCallback(
    async (taskId: string) => {
      try {
        await deleteTask(taskId).unwrap()
        toast.success('Task deleted successfully')
      } catch (error) {
        console.error('Failed to delete scheduled task:', error)
        toast.error('Failed to delete task. Please try again.')
      }
    },
    [deleteTask]
  )

  const handleUpdateTask = useCallback(
    async (id: string, input: UpdateScheduledTaskInput) => {
      try {
        await updateTask({ id, input }).unwrap()
        toast.success('Task updated successfully')
      } catch (error) {
        console.error('Failed to update task:', error)
        toast.error('Failed to update task. Please try again.')
      }
    },
    [updateTask]
  )

  const handleFreezeTask = useCallback(
    async (taskId: string) => {
      try {
        await updateTask({ id: taskId, input: { status: 'PAUSED' } }).unwrap()
        toast.success('Task frozen')
      } catch (error) {
        console.error('Failed to freeze task:', error)
        toast.error('Failed to freeze task. Please try again.')
      }
    },
    [updateTask]
  )

  const handleUnfreezeTask = useCallback(
    async (taskId: string) => {
      try {
        await updateTask({ id: taskId, input: { status: 'PENDING' } }).unwrap()
        toast.success('Task unfrozen')
      } catch (error) {
        console.error('Failed to unfreeze task:', error)
        toast.error('Failed to unfreeze task. Please try again.')
      }
    },
    [updateTask]
  )

  const handleSkipNextTask = useCallback(
    async (taskId: string) => {
      try {
        await skipNextTask(taskId).unwrap()
        toast.success('Skipped next run')
      } catch (error) {
        console.error('Failed to skip task:', error)
        toast.error('Failed to skip task. Please try again.')
      }
    },
    [skipNextTask]
  )

  const handleToggleFavourite = useCallback(
    async (taskId: string, current: boolean) => {
      try {
        await updateTask({ id: taskId, input: { bookmark: !current } }).unwrap()
      } catch (error) {
        console.error('Failed to toggle favourite:', error)
        toast.error('Failed to update favourite. Please try again.')
      }
    },
    [updateTask]
  )

  const handleToggleEmailNotification = useCallback(
    async (taskId: string, currentEnabled: boolean) => {
      try {
        await updateTask({
          id: taskId,
          input: { notificationSettings: { email: { enabled: !currentEnabled } } },
        }).unwrap()
      } catch (error) {
        console.error('Failed to toggle email notification:', error)
        toast.error('Failed to update notification settings. Please try again.')
      }
    },
    [updateTask]
  )

  // Tasks with executions, sorted: favourited first within each group,
  // then unseen (most unseen first), then by most recent run
  const { displayTasks, totalUnseen } = useMemo(() => {
    const all = tasksData?.tasks ?? []
    const withExecutions = all.filter((t) => t.lastRunAt != null || t.unseenResultCount > 0)
    const bookmarkDesc = (a: { bookmark: boolean }, b: { bookmark: boolean }) =>
      Number(b.bookmark) - Number(a.bookmark)

    const unseen = withExecutions
      .filter((t) => t.unseenResultCount > 0)
      .sort((a, b) => bookmarkDesc(a, b) || b.unseenResultCount - a.unseenResultCount)

    const seen = withExecutions
      .filter((t) => t.unseenResultCount === 0 && t.lastRunAt != null)
      .sort(
        (a, b) =>
          bookmarkDesc(a, b) || new Date(b.lastRunAt!).getTime() - new Date(a.lastRunAt!).getTime()
      )

    return {
      displayTasks: [...unseen, ...seen].slice(0, MAX_CARDS),
      totalUnseen: unseen.length,
    }
  }, [tasksData])

  if (isLoading) {
    return (
      <div className="container mx-auto flex flex-col px-3 md:px-10">
        <div className="mb-3 mt-2 flex items-center justify-between">
          <p
            className="text-left text-xl text-gray-900 md:text-2xl"
            style={{ fontFamily: 'Space Grotesk, sans-serif', letterSpacing: '0.01em' }}
          >
            Scheduled Tasks
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-48 rounded-3xl" />
          ))}
        </div>
      </div>
    )
  }

  if (displayTasks.length === 0) {
    return null
  }

  return (
    <>
      <div className="container mx-auto flex flex-col px-3 md:px-10">
        <div className="mb-3 mt-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <p
              className="text-left text-xl text-gray-900 md:text-2xl"
              style={{ fontFamily: 'Space Grotesk, sans-serif', letterSpacing: '0.01em' }}
            >
              Scheduled Tasks
            </p>
            {totalUnseen > 0 && (
              <span className="rounded-full bg-blue-500 px-2 py-0.5 text-xs font-medium text-white">
                {totalUnseen} new
              </span>
            )}
          </div>
          <Link
            href="/scheduled-tasks"
            className="text-sm text-gray-500 hover:text-gray-800 hover:underline"
          >
            View all
          </Link>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {displayTasks.map((task) => (
            <ScheduledTaskCard
              key={task.id}
              task={task}
              onClick={() => setSelectedTaskId(task.id)}
              onDelete={() => handleDeleteTask(task.id)}
              onFreeze={() => handleFreezeTask(task.id)}
              onUnfreeze={() => handleUnfreezeTask(task.id)}
              onSkipNext={() => handleSkipNextTask(task.id)}
              onToggleFavourite={() => handleToggleFavourite(task.id, task.bookmark)}
              onToggleEmailNotification={() =>
                handleToggleEmailNotification(
                  task.id,
                  task.notificationSettings?.email?.enabled ?? false
                )
              }
              formatRelativeTime={formatRelativeTime}
            />
          ))}
        </div>
      </div>

      {selectedTaskId && (
        <ScheduledTaskModal
          taskId={selectedTaskId}
          isOpen={!!selectedTaskId}
          onClose={() => setSelectedTaskId(null)}
          onUpdate={handleUpdateTask}
          onDelete={handleDeleteTask}
        />
      )}
    </>
  )
}
